<template>
  <NewStudioModalsModalShell class="save-modal" title="Save Design" data-figma-node="2909:175514" @close="$emit('close')">
    <form class="save-form" @submit.prevent="saveDesign">
      <label class="field">Design ID<div class="locked-field"><input :value="projectStore.designId || '—'" readonly disabled><img class="design-id-lock" src="/new-studio/icons/design-id-lock.svg" alt="Locked"></div></label>
      <div class="field"><span><label for="save-design-name">Design Name</label> <button class="field-help" type="button" aria-label="Naming help" aria-describedby="design-name-help">?</button><span id="design-name-help" class="sr-only">Choose a name used to identify this design.</span></span><input id="save-design-name" ref="nameInput" v-model.trim="name" placeholder="Give your design a name"></div>
      <label class="field"><span>Username <small>Will be shown publicly as the design author</small></span><input v-model.trim="username" placeholder="Username"></label>
      <p v-if="error" role="alert">{{ error }}</p>
      <button type="submit" :disabled="saving">{{ saving ? 'Saving…' : 'Save Design' }}</button>
      <button v-if="options?.continueWithoutSaving" class="cancel" type="button" :disabled="saving" @click="continueToCart">Continue Without Saving</button>
      <button v-else class="cancel" type="button" @click="$emit('close')">Cancel</button>
    </form>
  </NewStudioModalsModalShell>
</template>

<script setup lang="ts">
import { ModalType } from '~/stores/modal'
import { completeNewStudioSave, continueWithoutSaving } from '../logic'
defineEmits<{ close: [] }>()
const projectStore = useProjectStore()
const modalStore = useModalStore()
const loadingStore = useLoadingStore()
const options = computed(() => modalStore.getModalOptions(ModalType.SaveAs))
const name = ref(projectStore.designName || '')
const username = ref(projectStore.username || '')
const saving = ref(false)
const error = ref('')
const nameInput = useTemplateRef<HTMLInputElement>('nameInput')
const saveDesign = async () => {
  error.value = ''
  if (!name.value) { error.value = 'Please enter a design name.'; return }
  if (name.value.toLowerCase().includes('custom')) { error.value = "Design name cannot contain the word 'Custom'."; return }
  saving.value = true
  loadingStore.setLoading(true)
  const keepLoading = Boolean(options.value?.keepLoading)
  const originalDesignId = projectStore.designId
  const originalDetails = {
    designName: projectStore.designName || '',
    canFeature: projectStore.canFeature,
    commercial: projectStore.isCommercialProject,
    projectName: projectStore.projectName,
    salesperson: projectStore.salesperson,
    username: projectStore.username,
  }
  const restoreOriginal = () => {
    projectStore.setDesignId(originalDesignId)
    projectStore.updateDetails(originalDetails)
  }
  try {
    projectStore.updateDetails({ designName: name.value, username: username.value })
    if (options.value?.saveAs) projectStore.setDesignId(null)
    const response = await projectStore.saveDesign({ keepLoading }) as { success?: boolean; error?: unknown }
    if (!response?.success) { restoreOriginal(); error.value = 'Unable to save the design. Please try again.'; return }
    await completeNewStudioSave(modalStore, loadingStore, { keepLoading })
  } catch { restoreOriginal(); error.value = 'Unable to save the design. Please try again.' }
  finally {
    saving.value = false
    if (!keepLoading) loadingStore.setLoading(false)
  }
}
const continueToCart = async () => {
  error.value = ''
  saving.value = true
  try { await continueWithoutSaving(modalStore, ModalType.SaveAs) }
  catch { error.value = 'Unable to continue to the cart. Please try again.' }
  finally { saving.value = false }
}
onMounted(() => nextTick(() => nameInput.value?.focus()))
</script>

<style scoped>
.save-form { display: grid; gap: 17.95px; padding-top: 17.95px; }
.field { display: grid; gap: 8px; color: #1c1c1e; font-size: 17.052px; font-weight: 600; }
.field-help { position: relative; display: inline-grid; width: 24px; height: 24px; min-height: 24px; margin: -5px 0 -5px 4px; padding: 0; place-items: center; border: 0; background: transparent; color: transparent; vertical-align: 2px; }
.field-help::before { position: absolute; display: grid; width: 13.462px; height: 13.462px; place-items: center; border-radius: 50%; background: #e9e8e3; color: #8c8b86; content: '?'; font-size: 8.97px; font-weight: 700; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); }
.field small { margin-left: 12px; font-size: 13.462px; font-weight: 500; }
input:not([type='checkbox']) { box-sizing: border-box; width: 100%; height: 50.26px; padding: 0 18px; border: 2px solid #d9d8d3; background: #fff; color: #1c1c1e; font: inherit; font-size: 17.95px; font-weight: 600; }
.locked-field { position: relative; }
.locked-field input { padding-right: 52px; }
.design-id-lock { position: absolute; top: 50%; right: 18px; width: 17.95px; height: 17.95px; transform: translateY(-50%); }
p[role='alert'] { margin: 0; color: #a32121; font-size: 13px; }
button { min-height: 52.055px; border: 0; background: #2e3a46; color: #fff; font: inherit; font-size: 16.155px; font-weight: 700; cursor: pointer; }
button.cancel { border: 2px solid #e6e5e0; background: #fff; color: #8a8a86; }
button:disabled { opacity: 0.6; cursor: not-allowed; }
:global(.save-modal > h2) { margin-top: 17.95px; text-align: left; font-size: 34.105px; letter-spacing: -.341px; }
:global(.save-modal) { box-sizing: border-box; min-height: 560px; max-height: none !important; }
</style>
